<script>
  import { createEventDispatcher, onMount, onDestroy } from 'svelte';
  import { fade } from 'svelte/transition';
  
  const dispatch = createEventDispatcher();
  
  export let isOpen = false;
  export let title = '';
  export let headerClass = '';
  export let size = 'md'; // sm, md, lg, xl
  export let closable = true;
  export let closeOnEscape = true;
  export let closeOnBackdrop = true;
  export let centered = true;

  let modalElement;
  let previousFocus;

  function close() {
    if (!closable) return;
    isOpen = false;
    dispatch('close');
  }

  function handleKeydown(event) {
    if (event.key === 'Escape' && closeOnEscape) {
      close();
    }
  }

  function handleBackdropClick(event) {
    if (event.target === event.currentTarget && closeOnBackdrop) {
      close();
    }
  }

  // 포커스 관리
  function trapFocus(event) {
    if (!modalElement) return;
    
    const focusableElements = modalElement.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.key === 'Tab') {
      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          event.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement.focus();
          event.preventDefault();
        }
      }
    }
  }

  onMount(() => {
    if (isOpen) {
      previousFocus = document.activeElement;
      // 모달이 열릴 때 첫 번째 포커스 가능한 요소에 포커스
      setTimeout(() => {
        const firstFocusable = modalElement?.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (firstFocusable) {
          firstFocusable.focus();
        }
      }, 100);
    }
  });

  onDestroy(() => {
    // 모달이 닫힐 때 이전 포커스 복원
    if (previousFocus) {
      previousFocus.focus();
    }
  });

  $: if (isOpen) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }

  $: modalClass = [
    'modal',
    `modal-${size}`,
    centered && 'modal-centered'
  ].filter(Boolean).join(' ');
</script>

<svelte:window on:keydown={handleKeydown} />

{#if isOpen}
  <div 
    class="modal-overlay" 
    transition:fade={{ duration: 200 }}
    on:click={handleBackdropClick}
    on:keydown={trapFocus}
    role="dialog"
    aria-modal="true"
    aria-labelledby={title ? 'modal-title' : undefined}
  >
    <div 
      class={modalClass} 
      transition:fade={{ delay: 50, duration: 250 }}
      bind:this={modalElement}
    >
      {#if title || closable}
        <div class="modal-header {headerClass}">
          {#if title}
            <h3 class="modal-title" id="modal-title">{title}</h3>
          {/if}
          {#if closable}
            <button 
              class="modal-close" 
              on:click={close}
              aria-label="모달 닫기"
              type="button"
            >
              ×
            </button>
          {/if}
        </div>
      {/if}
      
      <div class="modal-body">
        <slot />
      </div>
      
      {#if $$slots.footer}
        <div class="modal-footer">
          <slot name="footer" />
        </div>
      {/if}
    </div>
  </div>
{/if}

<style>
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: var(--z-modal);
    backdrop-filter: blur(2px);
  }

  .modal {
    background: var(--color-white);
    border-radius: var(--border-radius-lg);
    box-shadow: var(--shadow-lg);
    max-width: 500px;
    width: 90%;
    height: auto;
    max-height: 90vh;
    overflow: hidden;
    position: relative;
    display: flex;
    flex-direction: column;
  }

  .modal-centered {
    margin: auto;
  }

  /* 모달 크기 변형 */
  .modal-sm {
    max-width: 300px;
  }

  .modal-md {
    max-width: 500px;
  }

  .modal-lg {
    max-width: 800px;
  }

  .modal-xl {
    max-width: 1200px;
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--spacing-md);
    border-bottom: 1px solid var(--border-color);
    background-color: var(--primary-color);
  }

  .modal-title {
    margin: 0;
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-semibold);
    color: white;
  }

  .modal-close {
    background: none;
    border: none;
    font-size: var(--font-size-xl);
    cursor: pointer;
    color: white;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--border-radius-md);
    transition: all 0.3s ease;
  }

  .modal-close:hover {
    background-color: rgba(255, 255, 255, 0.2);
    color: white;
    transform: scale(1.1);
  }

  .modal-body {
    padding: var(--spacing-lg);
    overflow-y: auto;
    flex-grow: 0;
  }

  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: var(--spacing-sm);
    padding: var(--spacing-lg);
    border-top: 1px solid var(--border-color);
    background-color: var(--color-gray-50);
  }

  /* 반응형 디자인 */
  @media (max-width: 768px) {
    .modal {
      width: 95%;
      max-width: 95vw;
      max-height: 95vh;
      margin: var(--spacing-sm);
    }
    
    .modal-lg,
    .modal-xl {
      max-width: 95vw;
    }
    
    .modal-header {
      padding: var(--spacing-md);
    }
    
    .modal-body {
      padding: var(--spacing-md);
    }
    
    .modal-footer {
      padding: var(--spacing-md);
      flex-direction: column;
      gap: var(--spacing-xs);
    }
  }

  @media (max-width: 480px) {
    .modal-overlay {
      padding: var(--spacing-xs);
      align-items: flex-start;
      padding-top: 10vh;
    }
    
    .modal {
      width: 100%;
      max-width: 100%;
      margin: 0;
    }
  }

  /* 접근성 향상 */
  .modal:focus {
    outline: none;
  }

  .modal-close:focus {
    outline: 2px solid var(--primary-color);
    outline-offset: 2px;
  }

  /* 애니메이션 줄이기 설정 존중 */
  @media (prefers-reduced-motion: reduce) {
    .modal {
      transition: none;
    }
  }
</style> 